import React, { useState } from "react";
import { ShieldAlert, ChevronDown } from "lucide-react";

/**
 * Friendly, plain-language disclaimer used at Signup and anywhere a user
 * starts a "contract" / agreement flow (job acceptance, listing a sale, etc).
 *
 * variant="signup"     -> general platform disclaimer (training + marketplace)
 * variant="marketplace" -> shorter, buyer/seller-risk-only version
 *
 * Usage:
 *   const [agreed, setAgreed] = useState(false);
 *   <Disclaimer variant="signup" checked={agreed} onChange={setAgreed} />
 *   // then block submit unless `agreed` is true
 */
export default function Disclaimer({ variant = "signup", checked, onChange, required = true }) {
  const [expanded, setExpanded] = useState(false);

  const SHORT = {
    signup:
      "PilotLink helps you study for state pilot car certification and connects drivers, companies, and equipment sellers — we're a platform, not the certifying authority, and not a party to any sale.",
    marketplace:
      "Marketplace deals are made directly between buyers and sellers. Inspect any item before you pay, just like you would on Offer Up, Facebook Marketplace, or Craigslist.",
  };

  const LONG = {
    signup: (
      <ul className="list-disc pl-4 space-y-1.5">
        <li>
          <span className="font-semibold">Training is exam prep, not certification.</span> Our courses and study materials are designed solely to assist with exam readiness. All official pilot-car certification is issued exclusively by the appropriate state or licensing authority.
        </li>
        <li>
          <span className="font-semibold">We're a matchmaking platform.</span> Job postings, driver profiles, and
          company listings are provided by users. PilotLink doesn't guarantee the accuracy of a listing, vet
          every company, or guarantee work — use your own judgment before accepting a job or hiring a driver.
        </li>
        <li>
          <span className="font-semibold">Marketplace transactions occur directly between users.</span> All equipment listings and purchases are user-to-user transactions. We are not a participant in any sale. Always meet in a safe location, inspect equipment prior to payment, and use secure, trackable payment methods whenever possible.
        </li>
        <li>
          <span className="font-semibold">You are responsible for regulatory compliance.</span> Pilot-car and escort requirements differ by state. Always confirm current regulations with your state Department of Transportation before accepting any assignment.
        </li>
      </ul>
    ),
    marketplace: (
      <ul className="list-disc pl-4 space-y-1.5">
        <li>PilotLink lists items for sale — we don't own, inspect, ship, or guarantee any item.</li>
        <li>Meet in a safe public place when possible, and check the item in person before paying.</li>
        <li>Use trackable payment methods. Be cautious of any buyer/seller asking for unusual payment types.</li>
        <li>If something feels off, trust your gut — the same advice you'd follow on any peer marketplace.</li>
      </ul>
    ),
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-400/20 dark:bg-amber-400/[0.06] p-3.5">
      <div className="flex items-start gap-2.5">
        <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="flex-1 min-w-0">
          <p className="text-[13px] leading-relaxed text-amber-900 dark:text-amber-100/90">
            {SHORT[variant]}{" "}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-0.5 font-semibold text-amber-700 dark:text-amber-300 underline underline-offset-2 hover:text-amber-800 dark:hover:text-amber-200"
            >
              {expanded ? "Show less" : "Read more"}
              <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
            </button>
          </p>

          {expanded && (
            <div className="mt-2.5 pt-2.5 border-t border-amber-200/70 dark:border-amber-400/15 text-[12.5px] leading-relaxed text-amber-900/90 dark:text-amber-100/80">
              {LONG[variant]}
            </div>
          )}

          {onChange && (
            <label className="mt-3 flex items-start gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!checked}
                onChange={(e) => onChange(e.target.checked)}
                required={required}
                className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500 cursor-pointer shrink-0"
              />
              <span className="text-[12.5px] font-medium text-amber-900 dark:text-amber-100/90">
                I understand and agree to these terms.
              </span>
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
